import React, { ReactElement, useEffect } from 'react';
import { isSome } from 'fp-ts/lib/Option';
import { useReadDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useReadDashboard';
import { dispatchDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { useReadWidgets } from 'adapter/in/dashboards/hooks/Widgets/useReadWidgets';
import { dispatchWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import ViewSelectorContainer from 'adapter/in/dashboards/containers/ViewSelectorContainer/ViewSelectorContainer';
import { Loading, Result, ResultVariants } from '@dexma/ui-components';
import i18n from 'asset/translation';
import { useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';

type EditLayoutContainerProps = {
    id: string;
};

const useEditLayoutContainer = (id: string) => {
    const { data, error, reset: readDashboardReset } = useReadDashboard(id);
    const { setDashboard } = dispatchDashboardEdit();
    const { editLayoutMode } = useLayoutsView();
    const { data: readWidgetsResult, error: readWidgetsError, reset: resetWidgets } = useReadWidgets(id);
    const { setWidgets } = dispatchWidgetsView();

    useEffect(() => {
        if (isSome(data)) {
            setDashboard(data);
        }
    }, [data]);

    useEffect(() => {
        if (isSome(readWidgetsResult)) {
            setWidgets(readWidgetsResult.value);
        }
    }, [readWidgetsResult]);

    const onUnmountResetState = () => {
        readDashboardReset();
        resetWidgets();
    };

    useEffect(() => onUnmountResetState, []);

    return {
        hasData: isSome(data) && isSome(readWidgetsResult),
        error: isSome(error) ? error : readWidgetsError,
        editLayoutMode,
    };
};

export const EditLayoutContainer = (props: EditLayoutContainerProps): ReactElement => {
    const { id } = props;
    const { editLayoutMode, error, hasData } = useEditLayoutContainer(id);

    if (hasData) {
        return <ViewSelectorContainer isEditMode dashboardId={id} isEditingLayout={editLayoutMode} />;
    }
    if (isSome(error)) {
        return (
            <Result
                variant={ResultVariants.ERROR}
                title={i18n.t('common.errorWrong')}
                info={i18n.t('common.errorWrongInfo')}
                icon='bot-dead'
            />
        );
    }
    return <Loading isLoading size={50} />;
};

export default EditLayoutContainer;
